import { createApp } from "vue";
// Styling
import "@/index.css";
import 'primeicons/primeicons.css'

// TMI-specific components, plugins & services
import App from "@/App.vue";

import ContentFiltersPlugin from "@/plugins/ContentFiltersPlugin";
import PrimeVuePlugin from "@/plugins/PrimeVuePlugin";
import SectorToolsPlugin from "@/plugins/SectorToolsPlugin";

import router, { myArticlesKey, myWebinarsKey, SectorsAsync, } from "@/router";

import PiniaPlugin from "@/plugins/PiniaPlugin";

import * as tmi from "@/constants";

import {
    articleKey,
    bonusReportKey,
    contentWrapperKey,
    courseKey,
    etfListingKey,
    fiveStarModelPortfolioKey,
    lessonsKey,
    majorIndexBreadthKey,
    modelPortfolioKey,
    notFoundKey,
    nyseBpiKey,
    portfolioBuilderKey,
    positionSizeCalculatorKey,
    relativeStrengthMatrixKey,
    scannerKey,
    sectorAlertsKey,
    sectorsKey,
    sssHistoryKey,
    sssScannerKey,
    tradeAlertKey,
    tradeAlertSearchKey,
    tradeOfTheDayKey,
    vimeoKey,
    webinarKey,
    discordKey,
    discordRegistrationKey,
} from "@/types/products/product-definitions";
import CachePlugin from "@/plugins/CachePlugin";
import LabelServicePlugin from "@/plugins/LabelServicePlugin";
import AdServicePlugin from "@/plugins/AdServicePlugin";
import MediaPlugin from "@/plugins/MediaPlugin";
import ProductContextsPlugin from "@/plugins/ProductContextsPlugin";

import { createHead } from '@unhead/vue/client'
import { PiniaColada } from "@pinia/colada";
import { piniaColadaOptions } from "@/queries/colada-options";
import { lazyView } from "@/utils/lazy-view";
import { warmPrivateLayout } from "@/layouts/private-layout";
import { scheduleZendeskWidget } from "@/services/zendesk-widget";
import { useAuthStore } from "@/stores/Auth";

// Make touch/wheel event listeners passive by default to improve scroll performance
// This fixes warnings from third-party libraries (PrimeVue) that don't mark their listeners as passive
( function makeEventListenersPassive() {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    const passiveEvents = new Set( [ 'touchstart', 'touchmove', 'wheel', 'mousewheel' ] );

    EventTarget.prototype.addEventListener = function (
        type: string,
        listener: EventListenerOrEventListenerObject | null,
        options?: boolean | AddEventListenerOptions
    ) {
        if ( passiveEvents.has( type ) ) {
            const newOptions: AddEventListenerOptions =
                typeof options === 'boolean'
                    ? { capture: options, passive: true }
                    : { ...options, passive: options?.passive ?? true };
            return originalAddEventListener.call( this, type, listener, newOptions );
        }
        return originalAddEventListener.call( this, type, listener, options );
    };
} )();

const unheadClient = createHead();

const app = createApp( App );

app.use( CachePlugin )
    .use( PiniaPlugin )
    .use( PiniaColada, piniaColadaOptions )
    .use( SectorToolsPlugin )
    .use( router )
    .use( ContentFiltersPlugin )
    .use( ProductContextsPlugin )
    .use( MediaPlugin )
    .use( PrimeVuePlugin )
    .use( LabelServicePlugin )
    .use( AdServicePlugin )
    .use( unheadClient );

// UX roadmap #20: the member layout is its own chunk. A returning member's first route (its chunk and the auth guard)
// resolves before App mounts; start the layout alongside it rather than after it.
if ( useAuthStore( PiniaPlugin ).isAuthenticated ) {
    warmPrivateLayout();
}

// UX roadmap #26: the support launcher loads after the page has, instead of competing with the boot.
scheduleZendeskWidget();

router.isReady().then( () => {
    // lazyView, not a bare defineAsyncComponent: a delayed skeleton while the chunk downloads and an error view if it
    // cannot, instead of an empty content area.

    // Product subcomponents
    app.component( articleKey, lazyView( () => import( '@/components/wrappers/ProductArticleSearchWrapper.vue' ) ) );
    app.component( bonusReportKey, lazyView( () => import( '@/components/wrappers/ProductBonusReportSearchWrapper.vue' ) ) );
    app.component( contentWrapperKey, lazyView( () => import( '@/components/wrappers/ProductContentWrapper.vue' ) ) );
    app.component( lessonsKey, lazyView( () => import( '@/components/wrappers/ProductCourseWrapper.vue' ) ) );
    app.component( modelPortfolioKey, lazyView( () => import( '@/components/portfolios/ModelPortfoliosTables.vue' ) ) );
    app.component( fiveStarModelPortfolioKey, lazyView( () => import( '@/components/portfolios/FiveStarModelPortfolioContainer.vue' ) ) );
    app.component( scannerKey, lazyView( () => import( '@/components/LaunchScanner.vue' ) ) );
    app.component( tradeAlertKey, lazyView( () => import( '@/components/wrappers/ProductTradeAlertSearchWrapper.vue' ) ) );
    app.component( webinarKey, lazyView( () => import( '@/components/wrappers/ProductWebinarSearchWrapper.vue' ) ) );
    app.component( vimeoKey, lazyView( () => import( '@/components/wrappers/ProductVimeoWrapper.vue' ) ) );

    // Shadow Spike Scanner workspace tabs (route /shadow-spike, see pages/ShadowSpikeScanner.vue)
    app.component( sssScannerKey, lazyView( () => import( '@/components/shadow-spike/ShadowSpikeScannerView.vue' ) ) );
    app.component( sssHistoryKey, lazyView( () => import( '@/components/shadow-spike/ShadowSpikeHistoryView.vue' ) ) );

    // Common Tool Subcomponents
    app.component( etfListingKey, lazyView( () => import( '@/components/EtfList.vue' ) ) );
    app.component( majorIndexBreadthKey, lazyView( () => import( '@/pages/Tools/MajorIndexBreadth.vue' ) ) );
    app.component( notFoundKey, lazyView( () => import( '@/components/errors/NotFoundError.vue') ) );
    app.component( nyseBpiKey, lazyView( () => import( '@/pages/BPI.vue' ) ) );
    app.component( portfolioBuilderKey, lazyView( () => import( '@/pages/Tools/PositionKeyTool.vue' ) ) );
    app.component( positionSizeCalculatorKey, lazyView( () => import( '@/pages/Tools/PositionSizeCalculator.vue' ) ) );
    app.component( relativeStrengthMatrixKey, lazyView( () => import( '@/pages/Tools/RsMatrix.vue' ) ) );
    app.component( sectorsKey, lazyView( SectorsAsync ) );
    // app.component( stockReplacementStrategyCalculatorKey, defineAsyncComponent( SrsCalculatorAsync ) );
    app.component( tmi.SpreadCalculatorKey, lazyView( () => import( '@/pages/Tools/SpreadCalculator.vue' ) ) );

    // Alerts Subcomponents
    app.component( sectorAlertsKey, lazyView( () => import( '@/components/sectors/SectorAlertsList.vue' ) ) );
    app.component( tradeAlertSearchKey, lazyView( () => import( '@/components/search/TradeAlertSearch.vue' ) ) );
    app.component( tradeOfTheDayKey, lazyView( () => import( '@/pages/TradeOfTheDay.vue' ) ) );

    app.component( myArticlesKey, lazyView( () => import( '@/components/layout/MyLibrary.vue' ) ) );
    app.component( myWebinarsKey, lazyView( () => import( '@/components/layout/MyLibrary.vue' ) ) );

    // Discord Subcomponents
    app.component( discordKey, lazyView( () => import( '@/components/discord/Discord.vue' ) ) );
    app.component( discordRegistrationKey, lazyView( () => import( '@/components/discord/DiscordCallback.vue' ) ) );

    // Subcomponents of priorly listed components
    app.component( courseKey, lazyView( () => import( '@/components/courses/CourseOverview.vue' ) ) );

    app.mount( "#app" );
} );
